import React, { useState, useEffect, useCallback, useMemo } from "react";
import GetCookie from "../../utils/GetCookie";
import ErrorPopup from "../../utils/popup/ErrorPopup";
import ProcessingPopup from "../../utils/popup/ProcessingPopup";
import SuccessPopup from "../../utils/popup/SuccessPopup";
import DocumentPreview from "../../utils/DocumentPreview";
import axios from "axios";
import { StatusBadge, StatusBadgeSm, StatusBadgeXSm } from "../../utils/StatusBadge";
import PaginationControls from "../../utils/PaginationControls";
import AddEditVM from './AddEditVM';
import TrimContent from '../../utils/TrimContent';
import ConfirmationModal from '../../utils/ConfirmationModal';
import VmMappingReviewModal from './VmMappingReviewModal';
import { useNavigate } from 'react-router-dom';
import authNavigate from '../../globalComponents/AuthNav';

const ListVM = ({
  selectedTemplate,
  fabric_id,
  onEditValueMessage,
  onAddValueMessage,
  fabricTreeData
}) => {
  // Value Messages states
  const [valueMessages, setValueMessages] = useState([]);
  const [vmTotal, setVmTotal] = useState(0);
  const [vmPage, setVmPage] = useState(1);
  const [vmSize, setVmSize] = useState(25);
  const [vmSearch, setVmSearch] = useState("");
  const [vmSearchInput, setVmSearchInput] = useState("");
  const [vmCategory, setVmCategory] = useState("");
  const [vmAddedBy, setVmAddedBy] = useState("");
  const [created_by_name, setCreatedByName] = useState("");
  const [vmRefreshing, setVmRefreshing] = useState(false);
  const [showAddVM, setShowAddVM] = useState(false);  
  const [showEditVM, setShowEditVM] = useState(false);
  const [editingVMData, setEditingVMData] = useState(null);

  // Configuration state - ADD THIS
  const [configuration, setConfiguration] = useState(null);

  // Available filter options
  const [categories, setCategories] = useState([]);
  const [subTypes, setSubTypes] = useState([]);
  const [addedByOptions] = useState(["HUMAN", "AI"]);

  // Document preview states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewDocumentName, setPreviewDocumentName] = useState(null);
  const [expandedDocuments, setExpandedDocuments] = useState({});
  const [showAIMapConfirm, setShowAIMapConfirm] = useState(false);
  const [mappingLevel, setMappingLevel] = useState("");

  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState("");
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

  // Delete confirmation states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [vmToDelete, setVmToDelete] = useState(null);

  // Multi-select states
  const [selectedVmIds, setSelectedVmIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  const [vmMappingStatus, setVmMappingStatus] = useState(null);
  const [showMappingReview, setShowMappingReview] = useState(false);

  // AI Generate states
  const [aiVmStatus, setAiVmStatus] = useState(null);
  const [showAIGenerateConfirm, setShowAIGenerateConfirm] = useState(false);

  const navigate = useNavigate();

  // HTTP headers with authorization
  const headers = useMemo(
    () => ({
      Authorization: GetCookie("access_token"),
    }),
    []
  );

  // Document preview handlers
  const handlePreviewDocument = (docId, documentName) => {
    setPreviewDocId(docId);
    setPreviewDocumentName(documentName);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDocId(null);
    setPreviewDocumentName(null);
  };

  // Value Message search handlers
  const handleVmSearchInputChange = (e) => {
    setVmSearchInput(e.target.value);
  };

  const handleVmSearch = () => {
    setVmSearch(vmSearchInput);
    setVmPage(1);
  };

  const handleVmKeyPress = (e) => {
    if (e.key === "Enter") {
      handleVmSearch();
    }
  };

  const fetchValueMessages = useCallback(async () => {
    if (vmRefreshing) return;

    if (!vmRefreshing) {
      setIsProcessingPopupVisible(true);
      setProcessingMessage("Fetching Value Messages");
    }

    try {
      const requestBody = {
        template_id: selectedTemplate,
        fabric_id,
        page: vmPage,
        size: vmSize,
      };

      if (vmCategory) {
        requestBody.category = vmCategory;
      }

      if (created_by_name) {
        requestBody.created_by_name = created_by_name;
      }

      if (vmAddedBy) {
        requestBody.added_by = String(vmAddedBy).toUpperCase();
      }


      const response = await axios.post(
        "/pceapi/genbi/value-message/list-value-messages",
        requestBody,
        { headers }
      );

      const data = response.data;

      if (data.status === "NotAuthorized") {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === "failed") {
        setIsProcessingPopupVisible(false);
        setVmRefreshing(false);
        setPopupErrorMessage(data.message || "Failed to load value messages");
        setIsErrorPopupVisible(true);
        return;
      }

      // UPDATED: Set configuration from response
      setConfiguration(data.configuration || null);
      setValueMessages(data.data || []);
      setVmTotal(data.total || 0);
      setAiVmStatus(data.configuration?.ai_vm_invoke || null);
      setVmMappingStatus(data.configuration?.vm_mapping_status || null);
      setSubTypes(data.sub_types || []);

      const uniqueCategories = [
        ...new Set((data.data || []).map((vm) => vm.type_name).filter(Boolean)),
      ];
      setCategories(uniqueCategories);
    } catch (error) {
      setPopupErrorMessage(error.message || "Failed to load value messages");
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setVmRefreshing(false);
    }
  }, [
    selectedTemplate,
    vmPage,
    vmSize,
    vmCategory,
    created_by_name,
    vmAddedBy,
    vmRefreshing,
    headers,
  ]);

  // Fetch value messages when template is selected or filters change
  useEffect(() => {
    if (selectedTemplate) {
      fetchValueMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTemplate,
    vmPage,
    vmSize,
    vmCategory,
    vmAddedBy,
    vmRefreshing,
    headers,
  ]);

  // Poll for status updates when mapping is RUNNING
  useEffect(() => {
    if (vmMappingStatus !== "RUNNING" || !selectedTemplate) return;
    const interval = setInterval(() => {
      fetchValueMessages();
    }, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vmMappingStatus, selectedTemplate]);

  // Auto-open review modal when mapping transitions from RUNNING to COMPLETED
  const [prevMappingStatus, setPrevMappingStatus] = useState(null);
  const [isPendingReview, setIsPendingReview] = useState(false);
  useEffect(() => {
    if (prevMappingStatus === "RUNNING" && vmMappingStatus === "COMPLETED") {
      setIsPendingReview(true);
      setShowMappingReview(true);
    }
    setPrevMappingStatus(vmMappingStatus);
  }, [vmMappingStatus]);

  // Show confirmation modal before AI generation
  const handleAIGenerateClick = () => {
    if (!selectedTemplate || aiVmStatus === "RUNNING") return;
    setShowAIGenerateConfirm(true);
  };

  const handleAIMapValueMessage = async () => {
    if (!mappingLevel.trim()) {
      setPopupErrorMessage("Please enter a level");
      setIsErrorPopupVisible(true);
      return;
    }

    setShowAIMapConfirm(false);
    setIsProcessingPopupVisible(true);
    setProcessingMessage("Starting AI mapping...");

    try {
      const response = await axios.post(
        "/pceapi/genbi/vm-mapping/run",
        {
          template_id: selectedTemplate,
          level: mappingLevel,
        },
        { headers }
      );

      const data = response.data;

      if (data.status === "NotAuthorized") {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === "failed") {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || "Failed to map value messages");
        setIsErrorPopupVisible(true);
        return;
      }

      setPopupSuccessMessage(data.message || "Value message mapping started successfully");
      setIsSuccessPopupVisible(true);
      fetchValueMessages();
    } catch (error) {
      setIsProcessingPopupVisible(false);
      setPopupErrorMessage(error.message || "Failed to map value messages");
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setMappingLevel("");
    }
  };

  // AI Generate Value Messages function
  const handleAIGenerate = async () => {
    setShowAIGenerateConfirm(false);
    
    if (!selectedTemplate || aiVmStatus === "RUNNING") return;

    setIsProcessingPopupVisible(true);
    setProcessingMessage("Starting AI generation...");

    try {
      const response = await axios.post(
        "/pceapi/genbi/value-message/generate-value-message",
        {
          template_id: selectedTemplate,
        },
        { headers }
      );

      const data = response.data;

      if (data.status === "NotAuthorized") {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === "failed") {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(
          data.message || "Failed to generate value messages"
        );
        setIsErrorPopupVisible(true);
        return;
      }

      setAiVmStatus("RUNNING");
      setPopupSuccessMessage(
        data.message || "Value messages generation started successfully"
      );
      setIsSuccessPopupVisible(true);
    } catch (error) {
      setIsProcessingPopupVisible(false);
      setPopupErrorMessage(
        error.message || "Failed to generate value messages"
      );
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  const handleVmRefresh = () => {
    setVmRefreshing(true);
    fetchValueMessages();
  };

const handleEditValueMessage = async (vmId) => {
  if (!vmId || !selectedTemplate) return;

  setIsProcessingPopupVisible(true);
  setProcessingMessage("Fetching value message details...");

  try {
    const vmData = valueMessages.find(vm => vm.id === vmId);
    if (vmData) {
      setEditingVMData(vmData);
      setShowEditVM(true);
    } else {
      setPopupErrorMessage("Value message not found");
      setIsErrorPopupVisible(true);
    }
  } catch (error) {
    setPopupErrorMessage(error.message || "Failed to fetch value message details");
    setIsErrorPopupVisible(true);
  } finally {
    setIsProcessingPopupVisible(false);
  }
};

const handleVmEditSuccess = (message) => {
  setShowEditVM(false);
  setEditingVMData(null);
  fetchValueMessages();
  setPopupSuccessMessage(message);
  setIsSuccessPopupVisible(true);
};

  const confirmDeleteVM = (vm) => {
    setVmToDelete(vm);
    setShowDeleteConfirm(true);
  };

  const handleDeleteVM = async () => {
    setShowDeleteConfirm(false);

    if (!vmToDelete) return;

    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Deleting value message`);

    try {
      const response = await axios.post(
        "/pceapi/genbi/value-message/delete-value-message",
        {
          template_id: selectedTemplate,
          vm_id: vmToDelete.id,
        },
        { headers }
      );

      if (response.data.status === "NotAuthorized") {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (response.data.status === "failed") {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(
          response.data.message || "Failed to delete value message"
        );
        setIsErrorPopupVisible(true);
        return;
      }

      setPopupSuccessMessage(
        response.data.message || "Value message deleted successfully"
      );
      setIsSuccessPopupVisible(true);

      fetchValueMessages();
    } catch (error) {
      setPopupErrorMessage(error.message || "Failed to delete value message");
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setVmToDelete(null);
    }
  };

  const toggleVmSelect = (vmId) => {
    setSelectedVmIds((prev) =>
      prev.includes(vmId) ? prev.filter((id) => id !== vmId) : [...prev, vmId]
    );
  };

  const toggleSelectAll = () => {
    const currentIds = getFilteredValueMessages().map((vm) => vm.id);
    const allSelected = currentIds.every((id) => selectedVmIds.includes(id));
    if (allSelected) {
      setSelectedVmIds((prev) => prev.filter((id) => !currentIds.includes(id)));
    } else {
      setSelectedVmIds((prev) => [...new Set([...prev, ...currentIds])]);
    }
  };

  const handleBulkDelete = async () => {
    setShowBulkDeleteConfirm(false);
    if (!selectedVmIds.length) return;

    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Deleting ${selectedVmIds.length} value message${selectedVmIds.length > 1 ? "s" : ""}...`);

    try {
      const response = await axios.post(
        "/pceapi/genbi/value-message/delete-value-message",
        { template_id: selectedTemplate, vm_ids: selectedVmIds },
        { headers }
      );

      if (response.data.status === "NotAuthorized") {
        setIsProcessingPopupVisible(false);
        authNavigate("/login", "iam", navigate);
        return;
      }

      if (response.data.status === "failed") {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(response.data.message || "Failed to delete value messages");
        setIsErrorPopupVisible(true);
        return;
      }

      setPopupSuccessMessage(response.data.message || "Value messages deleted successfully");
      setIsSuccessPopupVisible(true);
      setSelectedVmIds([]);
      fetchValueMessages();
    } catch (error) {
      setPopupErrorMessage(error.message || "Failed to delete value messages");
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  const handleAddValueMessage = () => {
    setShowAddVM(true);
  };

  const handleVmAddSuccess = () => {
    setShowAddVM(false);
    fetchValueMessages();
  };

  // Close popups
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };

  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
  };

  const getFilteredValueMessages = useCallback(() => {
    if (!vmSearch.trim()) {
      return valueMessages;
    }

    const searchTerm = vmSearch.toLowerCase();

    return valueMessages.filter((vm) => {
      const messageMatch = (vm.value_message || "")
        .toLowerCase()
        .includes(searchTerm);

      const categoryMatch = (vm.type_name || "")
        .toLowerCase()
        .includes(searchTerm);

      const addedByMatch = (vm.added_by || "")
        .toLowerCase()
        .includes(searchTerm);

      const subTypesMatch = (vm.sub_types || []).some((subType) =>
        (subType || "").toLowerCase().includes(searchTerm)
      );

      const documentsMatch = (vm.documents || []).some((doc) =>
        (doc.document_name || "").toLowerCase().includes(searchTerm)
      );

      return (
        messageMatch ||
        categoryMatch ||
        addedByMatch ||
        subTypesMatch ||
        documentsMatch
      );
    });
  }, [valueMessages, vmSearch]);

  return (
    <>
      <div className="ev-padding-small">
        {/* Value Messages Header */}
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <span className="ev-med-p-blue mb-1 me-2">Value Messages ({vmTotal})</span>
          </div>
          <div className="d-flex gap-2 mb-2">
            <div className="ev-message-card3 ev-icon-btn-padding-reg">
              <button
                className="ev-btn-gradient-fill-round avatar-tooltip ev-bold-reg ev-btn-xsmall"
                data-tooltip="Generate value messages using AI"
                onClick={handleAIGenerateClick}
                disabled={aiVmStatus === "RUNNING"}
              >
                <span>
                    <i className="bi bi-play-fill"></i> &nbsp;
                    {aiVmStatus === "RUNNING" ? "Generating..." : "AI Generate"}
                  </span>
              </button>
              <span className="ms-1">
                  <StatusBadgeXSm status={aiVmStatus} className="mt-0"/>
                </span>
            </div>

            <div className="ev-message-card3 ev-icon-btn-padding-reg">
              <button
                className="ev-btn-gradient-fill-round avatar-tooltip ev-bold-reg ev-btn-xsmall "
                data-tooltip="Map value messages using AI"
                onClick={() => {
                  setMappingLevel("");
                  setShowAIMapConfirm(true);
                }}
                disabled={vmMappingStatus === "RUNNING"}
              >
                  <span>
                    <i className="bi bi-diagram-3-fill"></i> &nbsp;
                    {vmMappingStatus === "RUNNING" ? "Mapping..." : "AI Map"}
                  </span>
              </button>
                <span className="ms-1">
                  <StatusBadgeXSm status={vmMappingStatus} className="mt-0"/>
                </span>
            </div>
            <button
              className="ev-btn-blue-outline-round ev-bold-reg ev-btn-xsmall"
              onClick={() => setShowMappingReview(true)}
            >
              <i className="bi bi-eye-fill me-1"></i> View Mapping
            </button>
            {selectedVmIds.length > 0 && (
              <button
                className="ev-btn-red-fill-round ev-bold-reg ev-btn-small"
                onClick={() => setShowBulkDeleteConfirm(true)}
              >
                <i className="bi bi-trash me-1"></i> Delete Selected ({selectedVmIds.length})
              </button>
            )}
            <button
              className="ev-btn-blue-outline-round ev-bold-reg  ev-btn-small"
              onClick={handleAddValueMessage}
            >
              <i className="bi bi-plus-circle me-1"></i> Add Value Message
            </button>

              {/* UPDATED: Pass configuration to AddEditVM */}
              {showAddVM && (
                <div className="modal fade show d-block bg-dark bg-opacity-50" >
                  <div className="modal-dialog modal-xl modal-dialog-centered">
                    <div className="modal-content ev-message-card2 d-block ev-card-2">
                      <AddEditVM
                        selectedTemplate={selectedTemplate}
                        fabric_id={fabric_id}
                        onClose={() => setShowAddVM(false)}
                        onSuccess={handleVmAddSuccess}
                        Configuration={configuration}
                        fabricTreeData={fabricTreeData}
                        subTypes={subTypes}
                      />
                    </div>
                  </div>
                </div>
              )}

              {showEditVM && editingVMData && (
                <div className="modal fade show d-block bg-dark bg-opacity-50">
                  <div className="modal-dialog modal-xl modal-dialog-centered">
                    <div className="modal-content  ev-message-card2 d-block ev-card-2">
                      <AddEditVM
                        editingVM={editingVMData}
                        selectedTemplate={selectedTemplate}
                        onClose={() => {
                        setShowEditVM(false);
                        setEditingVMData(null);
                        }}
                        onSuccess={handleVmEditSuccess}
                        Configuration={configuration} // Make sure this contains the configuration object
                        fabricTreeData={fabricTreeData}
                      />
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>

        <div className ="row mt-2 mb-0">
          <div className ="col-6">
            {/* Search Bar */}
                <input
                  type="text"
                  className="form-control search-input ms-0"
                  placeholder="Search messages"
                  value={vmSearchInput}
                  onChange={handleVmSearchInputChange}
                  onKeyPress={handleVmKeyPress}
                />
                <button
                  className="ev-btn-lightblue-fill-round search-bar-button"
                  onClick={handleVmSearch}
                >
                  <i className="bi bi-search"></i>
                </button>
          </div>
          <div className ="col-6 text-end">
              <button
                className="ev-btn-lightblue-fill-round ev-bold-reg ms-2"
                onClick={handleVmRefresh}
              >
                <i className="bi bi-arrow-repeat"></i> Refresh
              </button>
          </div>
        </div>

        {/* Value Messages Table */}
        <div className="table-container">
          <table className="table-custom1 mt-0 overflow-hidden w-100">
            <thead className="">
              <tr>
                <th style={{ width: "30px" }}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={getFilteredValueMessages().length > 0 && getFilteredValueMessages().every((vm) => selectedVmIds.includes(vm.id))}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="w-20">
                  <div className="d-flex">
                  <label className="ev-small-p-blue">Category </label>
                  <select
                    className="ev-message-card2  ev-btn-xsmall ev-fs-11 ms-2 w-40"
                    value={vmCategory}
                    onChange={(e) => {
                      setVmCategory(e.target.value);
                      setVmPage(1);
                    }}
                  >
                    <option value="">All</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  </div>
                </th>

                <th className="w-25">
                  <div className="d-flex">
                    <label className="ev-small-p-blue">Added By </label>
                    <select
                      className="ev-message-card2 ev-btn-xsmall ev-fs-11 ms-2 w-40"
                      value={vmAddedBy}
                      onChange={(e) => {
                        setVmAddedBy(e.target.value);
                        setVmPage(1);
                      }}
                    >
                      <option value="">All</option>
                      {addedByOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </th>
                <th className="w-45">
                  <label className="ev-small-p-blue">Value Message</label>
                </th>
                <th className="w-10"> 
                  <label className="ev-small-p-blue">Actions</label>
                </th>
              </tr>
            </thead>
          </table>
          
          <div className="scrollable-element dashboard-right-contentarea-table-height-1">
            <table className="table-custom1 overflow-hidden w-100">
              <tbody>
                {getFilteredValueMessages().length === 0 ? (
                  <div className="w-100 text-center">
                      <p className="ev-small-p-blue mt-5">
                        {aiVmStatus === "RUNNING"
                          ? "Generating value messages..."
                          : "No value messages found"}
                      </p>
                  </div>
                ) : (
                  getFilteredValueMessages().map((vm, index) => (
                  <tr
                    className="ev-card-2"
                    key={vm.id || index}
                  >
                    <td className="align-top" style={{ width: "30px" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectedVmIds.includes(vm.id)}
                        onChange={() => toggleVmSelect(vm.id)}
                      />
                    </td>
                    <td
                    className="align-top w-20"
                    >
                      <div className="mb-2">
                        <p className="ev-reg-p-blue ms-1 mb-1">
                          {vm.type_name}
                        </p>
                        {vm.sub_types &&
                          vm.sub_types.map((subType, idx) => (
                            <span
                              key={idx}
                              className="ev-message-card1 me-1 ev-btn-xsmall mt-1"
                            >
                              {subType}
                            </span>
                          ))}
                      </div>
                    </td>
                    <td  className="align-top w-25">
                      <div className="mt-1">
                        {vm.ai_human_generated === "AI" ? (
                          <span
                            className=" badge gradient-bg ev-fs-11 ev-bold-reg"
                          >
                            <i className ="bi bi-stars"></i> Generated using AI 
                          </span>
                        ) : (
                          <span
                            className="ev-message-card5 avatar-tooltip ev-fs-11"
                            data-tooltip={vm.created_by_email || "Unknown User"}

                          >
                            <TrimContent text={vm.created_by_email } maxLength={20} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="align-top w-45">
                      <p className="ev-xsmall-p-dark mb-2">{vm.value_message}</p>
                    <div className="mt-2">
                      <span className="ev-xsmall-p-blue">Referenced In</span>
                      <div className="flex mt-1">
                        {vm.documents && vm.documents.length > 0 ? (
                          <>
                            {(expandedDocuments[vm.id] ? vm.documents : vm.documents.slice(0, 3)).map((doc, idx) => (
                              <span
                                key={idx}
                                className="ev-message-card1 ev-fs-10 me-1 mb-1 pe-1"
                              >
                                {doc.document_name}
                                <button
                                  className="ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-reg ms-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePreviewDocument(
                                      doc.document_id,
                                      doc.document_name
                                    );
                                  }}
                                >
                                  <i className="bi bi-eye"></i> View
                                </button>
                              </span>
                            ))}
                            {vm.documents.length > 3 && (
                              <>
                                {!expandedDocuments[vm.id] && (
                                  <span className="ev-xsmall-p-blue me-1">
                                    + {vm.documents.length - 3} more
                                  </span>
                                )}
                                <button
                                  className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedDocuments(prev => ({
                                      ...prev,
                                      [vm.id]: !prev[vm.id]
                                    }));
                                  }}
                                >
                                  {expandedDocuments[vm.id] ? 'Hide' : 'Show all'}
                                </button>
                              </>
                            )}
                          </>
                        ) : (
                          <span className="ev-small-p-dark">
                            <i>No documents referenced </i>
                          </span>
                        )}
                      </div>
                    </div>
                    </td>
                    <td className="align-top w-10">
                      <div className="btn-group">
                        <button
                          className="btn btn-sm ev-btn-red-fill-round ev-radius-xsmall pe-2 ps-2 avatar-tooltip"
                          data-tooltip="Delete Value Message"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDeleteVM(vm);
                          }}
                          title="Delete Value Message"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                        <button
                          className="btn btn-sm ev-btn-skyblue-fill-round ev-radius-xsmall pe-2 ps-2 avatar-tooltip"
                          data-tooltip="Edit Value Message"
                          onClick={() => handleEditValueMessage(vm.id)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Value Messages Pagination */}
        {vmTotal > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              <span className="ev-small-p-dark">
                Showing {getFilteredValueMessages().length} of {vmTotal} value
                messages
                {vmSearch.trim() && ` (filtered by "${vmSearch}")`}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                className="ev-btn-light-fill"
                disabled={vmPage === 1}
                onClick={() => setVmPage(vmPage - 1)}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              <button className="ev-btn-light-fill" disabled>
                Page {vmPage} of {Math.max(1, Math.ceil(vmTotal / vmSize))}
              </button>
              <button
                className="ev-btn-light-fill"
                disabled={vmPage >= Math.ceil(vmTotal / vmSize)}
                onClick={() => setVmPage(vmPage + 1)}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
              <PaginationControls
                initialSize={vmSize}
                onSizeChange={(newSize) => {
                  setVmSize(newSize);
                  setVmPage(1); // RESET PAGE TO 1
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Document Preview Component */}
      <DocumentPreview
        isOpen={previewOpen}
        onClose={closePreview}
        docId={previewDocId}
        documentName={previewDocumentName}
      />

      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}

      {isErrorPopupVisible && (
        <ErrorPopup onClose={closeErrorPopup} message={popupErrorMessage} />
      )}

      {isSuccessPopupVisible && (
        <SuccessPopup
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && vmToDelete && (   
        <div
          className="modal fade show d-block bg-dark bg-opacity-50"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <p
                  className="mb-0 ev-med-p-dark"
                >
                  Confirm Delete Value Message
                </p>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteConfirm(false)}
                ></button>
              </div>
              <div className="modal-body ev-fs-12">
                <p>Are you sure you want to delete this value message?</p>
                <div
                  className="border p-2 mb-3 bg-light"
                >
                  <strong>Category:</strong> {vmToDelete.type_name}
                  <br />
                  <strong>Message:</strong>{" "}
                  {vmToDelete.value_message.length > 100
                    ? `${vmToDelete.value_message.substring(0, 100)}...`
                    : vmToDelete.value_message}
                </div>
                <p className="text-danger mb-0">
                  This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary ev-btn-reg"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger ev-radius-xsmall ev-btn-reg"
                  onClick={handleDeleteVM}
                >
                  Delete Value Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmationModal
        show={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={handleBulkDelete}
        title="Confirm Bulk Delete"
        message={`Are you sure you want to delete ${selectedVmIds.length} selected value message${selectedVmIds.length > 1 ? "s" : ""}? This action cannot be undone.`}
        confirmButtonText="Delete"
        confirmButtonClass="btn-danger"
      />

      {/* AI Generate Confirmation Modal */}
      <ConfirmationModal
        show={showAIGenerateConfirm}
        onClose={() => setShowAIGenerateConfirm(false)}
        onConfirm={handleAIGenerate}
        title="Confirm AI Value Message Generation"
        message="Are you sure you want to start generating value messages using AI?"
        confirmButtonText="Generate"
        confirmButtonClass="btn-primary"
      />

      {/* AI Map Value Message — Unified Modal */}
      {showAIMapConfirm && (
        <div className="modal fade show d-block bg-dark bg-opacity-50">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content ev-card-3">
              <div className="modal-header">
                <p className="mb-0 ev-med-p-dark">
                  AI Value Message Mapping
                </p>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAIMapConfirm(false);
                    setMappingLevel("");
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {/* Section Hierarchy Illustration */}
                <p className="ev-small-p-dark ev-bold-reg mb-2">How mapping levels work</p>
                <div className="mapping-info-container mb-3">
                  <div className="mapping-info-doc">
                    <div className="mapping-info-item mapping-info-item-l1">
                      1. Introduction
                      <span className="mapping-info-level-badge">Level 1</span>
                    </div>
                    <div className="mapping-info-item mapping-info-item-l2">
                      1.1 Background
                      <span className="mapping-info-level-badge">Level 2</span>
                    </div>
                    <div className="mapping-info-item mapping-info-item-l3">
                      1.1.1 Study Design
                      <span className="mapping-info-level-badge">Level 3</span>
                    </div>
                    <div className="mapping-info-item mapping-info-item-l2">
                      1.2 Objectives
                      <span className="mapping-info-level-badge">Level 2</span>
                    </div>
                    <div className="mapping-info-item mapping-info-item-l1">
                      2. Efficacy
                      <span className="mapping-info-level-badge">Level 1</span>
                    </div>
                  </div>
                </div>
                <p className="ev-xsmall-p-dark mb-2">
                  Level corresponds to section depth. "1.2" is Level 2, "1.2.3" is Level 3, and so on for deeper hierarchies.
                </p>
                <div className="ev-alert-card-warning mb-3">
                  <p className="mb-0">
                    <i className="bi bi-info-circle me-1"></i>
                    Mapping uses hierarchy inheritance — child sections without a direct mapping will inherit value messages from their closest mapped ancestor.
                  </p>
                </div>

                {/* Level Selection */}
                <div className="form-group">
                  <label className="form-label ev-small-p-dark">
                    Mapping Level <span className="text-danger">*</span>
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      id="mappingLevel"
                      className="formField3"
                      style={{ maxWidth: '80px' }}
                      placeholder="2"
                      value={mappingLevel}
                      onChange={(e) => setMappingLevel(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && mappingLevel.trim()) {
                          handleAIMapValueMessage();
                        }
                      }}
                    />
                    <div className="d-flex gap-1">
                      {["1", "2", "3"].map((lvl) => (
                        <button
                          key={lvl}
                          type="button"
                          className={`mapping-level-btn-quick ${mappingLevel === lvl ? "active" : ""}`}
                          onClick={() => setMappingLevel(lvl)}
                        >
                          {lvl}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary ev-btn-reg"
                  onClick={() => {
                    setShowAIMapConfirm(false);
                    setMappingLevel("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary ev-radius-xsmall ev-btn-reg"
                  onClick={handleAIMapValueMessage}
                  disabled={!mappingLevel.trim()}
                >
                  Start Mapping
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VM Mapping Review Modal */}
      <VmMappingReviewModal
        isOpen={showMappingReview}
        onClose={() => setShowMappingReview(false)}
        templateId={selectedTemplate}
        isPendingReview={isPendingReview}
        onConfirmSuccess={() => {
          setIsPendingReview(false);
          setShowMappingReview(false);
          fetchValueMessages();
        }}
      />
    </>
  );
};

export default ListVM;